import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { Observable, skip, take, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import {
  FilterInterface,
  paymentLPFilter,
} from "src/app/shared/data/filter-data";
import {
  PaymentFrequency,
  PaymentFrequencyEnumMapping,
} from "src/app/shared/enum/payment-frequency";
import {
  PaymentStatus,
  PaymentStatusToShow,
} from "src/app/shared/enum/paymentStatus";
import {
  PricingMethod,
  PricingMethodEnumMapping,
} from "src/app/shared/enum/pricing-method";
import { AdvertisingCampaignsPaymentLP } from "src/app/shared/models/advertising-campaigns/advertising-campaigns-payment-LP.interface";
import { IColumn } from "src/app/shared/models/table/column.interface";
import { BadgeService } from "src/app/shared/services/badge.service";
import { JwtService } from "src/app/shared/services/jwt.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import {
  resultLocationPartnerAdvertisingCampignFilterSelector,
  resultLoadingLocationAdsSelector,
} from "src/app/store/locationPartner/location-partner.selectors";
import { invokeAdvertisingCampaignRemunerationForLocationPartner } from "src/app/store/locationPartner/location-partner.action";
import { invokeApiDistroyed } from "src/app/store/apiState.interface";
import { PartnerStatus } from "src/app/shared/enum/partner-status";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { RefreshButtonState } from "src/app/shared/enum/refreshButtonState";
import {
  RemunerationEnum,
  RemunerationEnumMapping,
} from "src/app/shared/enum/renumeration.enum";
import { FiltersService } from "src/app/shared/services/filters.service";
import { FilterParams } from "src/app/shared/models/filterParams";
import { AdvertisingCampaignRemunerationDetails } from "src/app/shared/models/advertising-campaigns/advertisingCampaignRemunerationDetails.interface";
import { RemunerationFilterDto } from "src/app/shared/models/franchise/remunerationFilterDto.interface";
import { Constants } from "src/app/shared/data/constants";
import { UtilFunctions } from "src/app/utils/utilFunctions";
import { Authorities } from "src/app/shared/enum/authorities";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe } from "@angular/common";

@Component({
  selector: "app-remuneration",
  templateUrl: "./remuneration.component.html",
  styleUrls: ["./remuneration.component.scss"],
})
export class RemunerationComponent extends BaseComponent implements OnInit {
  token: string;
  views: number;
  columns: IColumn[] = [
    {
      key: "titre",
      label: "admin.locations.campaigns.campaignName",
      sorter: true,
    },
    {
      key: "startDate",
      label: "admin.locations.payment.campaignStartDate",
      sorter: true,
    },
    {
      key: "endDate",
      label: "admin.locations.payment.campaignEndDate",
      sorter: true,
    },
    {
      key: "impression",
      label: "admin.locations.payment.impressionsNumber",
      sorter: true,
    },
    {
      key: "status",
      label: "admin.locations.campaigns.status",
      sorter: true,
    },
    {
      key: "remuneration",
      label: "campaignDetails.remuniration",
      sorter: false,
    },
    {
      key: "TTC",
      label: "admin.locations.payment.totalTTC",
      sorter: true,
    },
  ];
  pricingMethod = PricingMethodEnumMapping;
  PaymentFrequency = PaymentFrequencyEnumMapping;
  isPaid: boolean | null = null;
  formFilter: FormGroup;

  advertisingCampaigns: AdvertisingCampaignRemunerationDetails[] = [];
  filterPayment: FilterInterface = paymentLPFilter;
  loadingAds$: Observable<boolean | null>;
  partnerStatus = PartnerStatus;
  cachedAt: Date | null;
  remunerationEnumMapping = RemunerationEnumMapping;
  fetchingCampaignsSate: RefreshButtonState = RefreshButtonState.IDLE;
  filters: FilterParams | null;

  advertisingCampaignPayment$: Observable<AdvertisingCampaignsPaymentLP | null>;
  resultAdvertisingCampaignPayment: AdvertisingCampaignsPaymentLP = {
    advertisingCampaigns: [],
    cachedAt: null,
    nextPayment: 0,
    partnerStatus: PartnerStatus.REVENUE_SHARING,
    paymentFrequency: PaymentFrequency.ANNUAL,
    pilotPhaseEndDate: new Date(),
    price: 0,
    pricingMethod: PricingMethod.NONE,
  };
  remunerationFilter: RemunerationFilterDto = {
    companyName: "",
    endDate: null,
    isPaid: null,
    pager: {
      pageNumber: 1,
      pageSize: Constants.defaultTableSize,
    },
    startDate: null,
  };

  forbidden: boolean;
  constructor(
    private store: Store<AppStateInterface>,
    private jwtService: JwtService,
    public badgeService: BadgeService,
    private fb: FormBuilder,
    private saveHistory: SaveUserHistoryService,
    private filtersService: FiltersService,
    private translate: TranslateService,
    private datePipe: DatePipe
  ) {
    super(store);

    this.advertisingCampaignPayment$ = this.store
      .pipe(select(resultLocationPartnerAdvertisingCampignFilterSelector))
      .pipe(takeUntil(this.ngDestroyed$));

    this.loadingAds$ = this.store
      .pipe(select(resultLoadingLocationAdsSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.formFilter = this.fb.group({
      companyName: [""],
      date: [[null, null]],
    });

    this.extractFilters();
  }

  extractFilters() {
    this.filters = this.filtersService.decodeUri();

    if (this.filters) {
      this.remunerationFilter.isPaid =
        this.findOption(this.filters.status) ?? null;
    }
    this.filtersService.patchFormValue(this.filters, ["date"], this.formFilter);

    this.remunerationFilter = {
      ...this.remunerationFilter,
      endDate: this.formFilter.get("date")?.value[1],
      startDate: this.formFilter.get("date")?.value[0],
    };

    const authorities = this.jwtService.getPermissions(
      this.jwtService.getToken()
    );
    if (authorities) {
      this.forbidden = !authorities.includes(
        Authorities.LP_PAYMENT_GETALL.toString()
      );
    }
  }

  ngOnInit(): void {
    console.log("remunerationEnumMapping");
    this.fetchCampaigns(false);
    this.advertisingCampaignPayment$.subscribe((result) => {
      if (result) {
        this.cachedAt = result.cachedAt;
        this.resultAdvertisingCampaignPayment = result;
        this.advertisingCampaigns = result.advertisingCampaigns;
      }
    });

    this.filterPayment.name.map(
      (e) =>
        (e.data = Object.keys(PaymentStatus)
          .filter((value) => isNaN(Number(value)) === true)
          .map(
            (key: string | any) =>
              PaymentStatusToShow[key as keyof typeof PaymentStatus]
          ))
    );

    this.saveHistory.saveUserHistory(
      UserHistoryActionsEnum.GET,
      UserHistoryServicesEnum.REMUNERATION_LP
    );

    this.formFilter.valueChanges.subscribe((changes) => {
      this.remunerationFilter = {
        ...this.remunerationFilter,
        startDate: changes.date[0],
        endDate: changes.date[1],
      };
      this.fetchCampaigns(false);
    });
  }

  findOption(option: any | null) {
    const KeyFound = Object.keys(PaymentStatusToShow).find(
      (key) => PaymentStatusToShow[key as PaymentStatus] === option
    );
    switch (KeyFound) {
      case PaymentStatus.PAID:
        this.isPaid = true;
        break;
      case PaymentStatus.UNPAID:
        this.isPaid = false;
        break;
      default:
        this.isPaid = null;
    }
    return this.isPaid;
  }
  saveOptionFilter(option: any | null) {
    this.remunerationFilter = {
      ...this.remunerationFilter,
      isPaid: this.findOption(option),
    };
    this.fetchCampaigns(false);
  }

  fetchCampaigns(refreshCache: boolean) {
    this.store.dispatch(invokeApiDistroyed());
    if (refreshCache) {
      this.fetchingCampaignsSate = RefreshButtonState.LOADING;
    }

    if (refreshCache) {
      this.advertisingCampaignPayment$
        .pipe(skip(1), take(1))
        .subscribe((result) => {
          if (result) {
            this.fetchingCampaignsSate = RefreshButtonState.SUCCESS;
            setTimeout(
              () => (this.fetchingCampaignsSate = RefreshButtonState.IDLE),
              2500
            );
          } else {
            this.fetchingCampaignsSate = RefreshButtonState.IDLE;
          }
        });
    }
    this.store.dispatch(
      invokeAdvertisingCampaignRemunerationForLocationPartner({
        remunerationFilter: {
          ...this.remunerationFilter,
          isPaid: this.isPaid,
        },
        refreshCache: refreshCache,
      })
    );
  }

  showTooltip(remunerationStatus: RemunerationEnum): boolean {
    return UtilFunctions.showTooltip(remunerationStatus);
  }

  getRevenueTooltip(
    paidImpressions: number,
    paidPeriodStart: Date,
    paidPeriodEnd: Date
  ): string {
    return UtilFunctions.getRevenueTooltip(
      paidImpressions,
      paidPeriodStart,
      paidPeriodEnd,
      this.translate,
      this.datePipe
    );
  }
}
